import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import payrollnootLogoLg from "assets/images/payrollnoot-logo-lg.png";
import routes from "constants/routes";
import axiosInstance from "../../axios/axiosInstance";
import useBranding from "hooks/useBranding";
import "./login.scss";

const tenantConfig = {
    partner: {
        label: "Partner",
        description: "Login into your partner account",
        redirectTo: "/dashboard",
        color: "#7c3aed",
    },
    corporation: {
        label: "Corporation",
        description: "Login into your corporate account",
        redirectTo: "/dashboard",
        color: "#0ea5e9",
    },
    company: {
        label: "Company",
        description: "Login into your employer account",
        redirectTo: "/dashboard",
        color: "#E7730A",
    },
    employee: {
        label: "Employee",
        description: "Login into your employee account",
        redirectTo: routes.EMPLOYEE_PORTAL.HOME,
        color: "#10b981",
    },
    contractor: {
        label: "Contractor",
        description: "Login into your contractor account",
        redirectTo: routes.CONTRACTOR_PORTAL.HOME,
        color: "#6366f1",
    },
    superadmin: {
        label: "Platform Admin",
        description: "PayrollNoot platform administration",
        redirectTo: "/superadmin",
        color: "#1a3a5c",
    },
};

const LoginForm = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const config = tenantConfig[type] || tenantConfig.company;
    const branding = useBranding();
    const logoSrc = branding.loginLogoUrl || branding.logoUrl || payrollnootLogoLg;
    const productName = branding.productName;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isPendingApproval, setIsPendingApproval] = useState(false);

    // MFA second-factor state
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState("");
    const [mfaPendingData, setMfaPendingData] = useState(null); // holds login response until MFA verified
    const [mfaLoading, setMfaLoading] = useState(false);

    // Forgot password modal state
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState("");
    const [forgotSent, setForgotSent] = useState(false);

    const completeLogin = (data) => {
        const { token, role, companyId, employeeId, contractorId } = data;
        localStorage.setItem("authToken", token);
        localStorage.setItem("userRole", role);
        localStorage.setItem("userEmail", email);
        if (companyId) localStorage.setItem("companyId", companyId);
        if (employeeId) localStorage.setItem("employeeId", employeeId);
        if (contractorId) localStorage.setItem("contractorId", contractorId);
        if (data.username) localStorage.setItem("userName", data.username);
        else if (data.firstName || data.name) {
            const name = data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim();
            if (name) localStorage.setItem("userName", name);
        }
        if (data.firmId != null) localStorage.setItem("firmId", String(data.firmId));
        if (data.firmRoleId != null) localStorage.setItem("firmRoleId", String(data.firmRoleId));
        if (data.firmRoleName) localStorage.setItem("firmRoleName", data.firmRoleName);
        if (data.features) localStorage.setItem("userFeatures", JSON.stringify(data.features));
        if (data.companies) localStorage.setItem("userCompanies", JSON.stringify(data.companies));
        if (data.branding) localStorage.setItem("branding", JSON.stringify(data.branding));
        if (data.locationId) localStorage.setItem("locationId", data.locationId);
        if (data.locationName) localStorage.setItem("locationName", data.locationName);
        if (role === "Employee") {
            navigate(routes.EMPLOYEE_PORTAL.HOME);
        } else if (role === "Contractor") {
            navigate(routes.CONTRACTOR_PORTAL.HOME);
        } else if (role === "SuperAdmin") {
            navigate("/superadmin");
        } else {
            navigate("/dashboard");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsPendingApproval(false);
        if (!email || !password) {
            setError("Please enter your email and password.");
            return;
        }
        setLoading(true);
        try {
            const res = await axiosInstance.post("/auth/login", { email, password });
            const { role } = res.data;

            const roleMap = { employee: "Employee", contractor: "Contractor", superadmin: "SuperAdmin" };
            const expectedRole = roleMap[type] || "Company";
            // Accountants can log in via /login/accountant or /login/company
            const allowedRoles = expectedRole === "Company" ? ["Company", "Accountant"] : [expectedRole];
            if (!allowedRoles.includes(role)) {
                setError("Incorrect Credentials.");
                return;
            }

            // Check if backend requires MFA verification
            if (res.data.mfaRequired) {
                setMfaPendingData(res.data);
                setMfaRequired(true);
                return;
            }

            completeLogin(res.data);
        } catch (err) {
            const msg = err?.response?.data?.message;
            if (err?.response?.data?.pendingApproval) {
                setIsPendingApproval(true);
                setError("Your account is pending approval by your employer. You will receive an email once your account is activated.");
            } else {
                setError(msg || "Incorrect Credentials.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!mfaCode || mfaCode.length !== 6) {
            setError("Please enter your 6-digit authenticator code.");
            return;
        }
        setMfaLoading(true);
        try {
            const res = await axiosInstance.post("/auth/mfa/login-verify", {
                email,
                code: mfaCode,
            });
            completeLogin(res.data.token ? res.data : mfaPendingData);
        } catch (err) {
            const msg = err?.response?.data?.message;
            setError(msg || "Invalid authentication code. Please try again.");
        } finally {
            setMfaLoading(false);
        }
    };

    const openForgot = () => {
        setForgotEmail(email);
        setForgotError("");
        setForgotSent(false);
        setShowForgot(true);
    };

    const closeForgot = () => setShowForgot(false);

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setForgotError("");
        if (!forgotEmail) {
            setForgotError("Please enter your email address.");
            return;
        }
        setForgotLoading(true);
        try {
            await axiosInstance.post("/auth/forgot-password", { email: forgotEmail });
            setForgotSent(true);
        } catch {
            setForgotError("Something went wrong. Please try again.");
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="login-page">
            {/* Left Panel */}
            <div className="login-left">
                <div className="login-left-inner">
                    <div className="login-preview-card">
                        <div className="preview-header">
                            <img src={logoSrc} alt={productName} className="preview-logo" />
                        </div>
                        <div className="login-form-preview-content">
                            <div className="login-form-preview-icon" style={{ background: config.color }}>
                                {config.label[0]}
                            </div>
                            <div className="login-form-preview-title">{config.label} Portal</div>
                            <div className="login-form-preview-desc">{config.description}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel */}
            <div className="login-right">
                <div className="login-right-inner">
                    <div className="login-brand">
                        <div className="login-brand-logo">
                            <img src={logoSrc} alt={productName} />
                        </div>
                    </div>

                    <button
                        className="login-back-btn"
                        onClick={() => navigate(routes.LOGIN.HOME)}
                    >
                        ← Back to login options
                    </button>

                    <div className="login-form-header">
                        <div className="login-type-badge" style={{ background: config.color + '18', color: config.color, borderColor: config.color + '40' }}>
                            {config.label}
                        </div>
                        <h2 className="login-heading" style={{ marginTop: 12 }}>
                            Sign in to your account
                        </h2>
                        <p className="login-subheading">{config.description}</p>
                    </div>

                    {error && (
                        <div className={isPendingApproval ? undefined : "login-error"}
                            style={isPendingApproval ? {
                                background: '#fef9c3', border: '1px solid #fbbf24',
                                borderRadius: 8, padding: '10px 14px',
                                color: '#92400e', fontSize: 13, marginBottom: 12,
                                lineHeight: 1.5
                            } : undefined}>
                            {isPendingApproval && <strong>⏳ Pending Approval — </strong>}
                            {error}
                        </div>
                    )}

                    {/* MFA Second Factor Step */}
                    {mfaRequired ? (
                        <form className="login-form" onSubmit={handleMfaSubmit}>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <div style={{ fontSize: 36, marginBottom: 8 }}>🔐</div>
                                <p style={{ fontSize: 14, color: '#555', margin: 0 }}>
                                    Two-factor authentication is enabled. Enter the 6-digit code from your authenticator app.
                                </p>
                            </div>
                            <div className="login-field">
                                <label className="login-label">Authenticator Code</label>
                                <input
                                    className="login-input"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={mfaCode}
                                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    autoFocus
                                    style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: 22 }}
                                />
                            </div>
                            <button
                                type="submit"
                                className="login-submit-btn"
                                style={{ background: config.color }}
                                disabled={mfaLoading}
                            >
                                {mfaLoading ? "Verifying..." : "Verify Code"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMfaRequired(false); setMfaCode(""); setError(""); }}
                                style={{ background: "none", border: "none", color: "#6c757d", fontSize: 13, cursor: "pointer", marginTop: 8, display: "block", width: "100%" }}
                            >
                                ← Back to login
                            </button>
                        </form>
                    ) : (

                    <form className="login-form" onSubmit={handleSubmit}>
                        <div className="login-field">
                            <label className="login-label">Email Address</label>
                            <input
                                className="login-input"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="login-field">
                            <label className="login-label">Password</label>
                            <div className="login-input-wrap">
                                <input
                                    className="login-input"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="login-toggle-pw"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        <div className="login-forgot">
                            <button
                                type="button"
                                onClick={openForgot}
                                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#E7730A", fontSize: "0.85rem", fontWeight: 600, fontFamily: "inherit" }}
                            >
                                Forgot password?
                            </button>
                        </div>

                        <button
                            type="submit"
                            className="login-submit-btn"
                            style={{ background: config.color }}
                            disabled={loading}
                        >
                            {loading ? "Signing in..." : `Sign in as ${config.label}`}
                        </button>
                    </form>
                    )} {/* end mfaRequired conditional */}
                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgot && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}
                    onClick={closeForgot}
                >
                    <div
                        style={{ background: "#fff", borderRadius: 16, padding: "32px 28px", width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
                        onClick={e => e.stopPropagation()}
                    >
                        {forgotSent ? (
                            <div style={{ textAlign: "center" }}>
                                <div style={{ width: 52, height: 52, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 13l4 4L19 7" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1D2630", margin: "0 0 10px" }}>Check your email</h3>
                                <p style={{ fontSize: 14, color: "#5B6B79", lineHeight: 1.6, margin: "0 0 24px" }}>
                                    If <strong>{forgotEmail}</strong> is registered, we've sent a password reset link. Check your inbox and spam folder.
                                </p>
                                <button className="login-submit-btn" style={{ background: "#E7730A" }} onClick={closeForgot}>
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                                    <div>
                                        <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1D2630", margin: 0 }}>Forgot Password?</h3>
                                        <p style={{ fontSize: 13, color: "#5B6B79", margin: "6px 0 0" }}>Enter your email and we'll send a reset link.</p>
                                    </div>
                                    <button onClick={closeForgot} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
                                </div>

                                {forgotError && <div className="login-error" style={{ marginBottom: 14 }}>{forgotError}</div>}

                                <form onSubmit={handleForgotSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    <div className="login-field">
                                        <label className="login-label">Email Address</label>
                                        <input
                                            className="login-input"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={forgotEmail}
                                            onChange={e => setForgotEmail(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="login-submit-btn"
                                        style={{ background: "#E7730A" }}
                                        disabled={forgotLoading}
                                    >
                                        {forgotLoading ? "Sending…" : "Send Reset Link"}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginForm;
